import { Pencil } from "lucide-react";
import React, { useCallback, useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter, NavLink } from "react-router-dom";

import { PageTitle } from "../../components/Common/PageTitle";
import { InfiniteScroller } from "../../components/InfiniteScroller";
import { MediaItem } from "../../components/MediaComponents/MediaItem";
import { ModalUploadMedia } from "../../components/MediaComponents/ModalUploadMedia";
import { MediaData } from "../../models/MediaData";
import DataSvc from "../../services/dataSvc";
import useLocation from "../FacilityDetailsPage/hooks/useLocation";

import "./styles.scss";

interface MatchParams {
  locationId?: string;
}

type IMediaPageProps = RouteComponentProps<MatchParams>;

const MediaPage: React.FunctionComponent<IMediaPageProps> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`mediaPage.${key}`);

  const locationId = props.match.params.locationId ?? "";

  const { data: locationData } = useLocation(locationId);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const refreshScrollerRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    if (locationId) {
      ReactGA.event({
        category: "facility_details",
        action: "View All Media",
      });
    }
  }, [locationId]);

  const fetchMedia = useCallback(async (params: any) => {
    const media = await DataSvc.getMedia(params);
    return { data: media as MediaData[], count: 0 };
  }, []);

  return (
    <div className="right-content media-page">
      <div className="back-top-bar">
        <NavLink to={`/location/${locationId}`} className="back-link">
          <i className="icons icon-back"></i>
          {t("back_to_facility_details")}
        </NavLink>
      </div>

      {locationData && (
        <>
          <div className="location-name">
            <PageTitle text={locationData.name} />
            <button
              className="btn btn-blue btn-new-media"
              onClick={() => setShowUploadModal(true)}
            >
              <Pencil size={16} color="white" className="pencil-icon" />
              {t("post")}
            </button>
          </div>

          <div className="section-title">{t("facility_latest_media")}</div>
          <div className="recent-media-container">
            <InfiniteScroller
              direction="horizontal"
              pageSize={10}
              dependencyKey={`${locationId}`}
              fetchFunction={fetchMedia}
              initialParams={{
                order: "desc",
                sort: "post_date",
                seek_total_count: true,
                location_id: [locationId],
              }}
              noItemsComponent={<div>{t("no_media_available")}</div>}
              onRefresh={(fn) => { refreshScrollerRef.current = fn; }}
              renderItem={(item: any) => (
                <MediaItem
                  key={item.id}
                  mediaItem={item as MediaData}
                  hideLocationName
                />
              )}
            />
          </div>
        </>
      )}
      {showUploadModal && (
        <ModalUploadMedia
          locationId={locationId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => refreshScrollerRef.current?.()}
        />
      )}
    </div>
  );
};

export default withRouter(MediaPage);
